import { useUrlSearchParams, createInjectionState } from "@vueuse/core";
import { IProfileShopFiltersResponse, IProfileShopProfilesResponse, profileShopQueryOptions } from "../services/api";
import { logger } from "@/services/logger";
import { computed, ref, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useDebounceFn } from "@vueuse/core";
import { t } from "@/locales/vue";

import { funnelService } from "@/services/funnelService";
import { getUnicodeBCP47 } from "@/locales/shared";
import {
  getPriceObjFromAmount,
  Price,
  priceAsNumber,
  priceDisplay,
} from "@/features/pricing/components/IAIPrice/IAIPrice.vue";
import { useProgressAnimation } from "./useProgressAnimation";

export interface ProfileShopFiltersState {
  type: string[];
  style: string[];
  format: string[];
  genres: string[];
  sort: string;
  price: {
    min: Price;
    max: Price;
  };
}

export type ProfileShopFiltersParams = Omit<ProfileShopFiltersState, "price"> & {
  price: number[];
};
const lang = getUnicodeBCP47();

export const ToggleFilterSource = {
  QuickFilter: "quick_filter",
  FilterPanel: "filter_panel",
} as const;

export type ToggleFilterSource = (typeof ToggleFilterSource)[keyof typeof ToggleFilterSource];

export const FilterResetSource = {
  EmptyState: "empty_state",
  FilterPanel: "filter_panel",
} as const;

export type FilterResetSource = (typeof FilterResetSource)[keyof typeof FilterResetSource];
type FilterEventName = "profile_shop_filters_selected" | "profile_shop_low_results";

export interface QuickFilter {
  group: keyof ProfileShopFiltersState;
  label: string;
  key: string;
  isActive: boolean;
}

const FILTER_GROUPS = ["type", "style", "format", "genres", "price"] as const;
export type FilterKey = (typeof FILTER_GROUPS)[number];

const DEBOUNCE_TIME = 500;
export const PAGE_SIZE = 100;
export const MAX_LOW_RESULTS = 5;

const [provideProfileShopFilters, _useProfileShopFilters] = createInjectionState(
  (
    filtersList: IProfileShopFiltersResponse,
    initialProfiles: IProfileShopProfilesResponse,
    initialFilters: ProfileShopFiltersState
  ) => {
    const resetState = computed<ProfileShopFiltersState>(() => ({
      type: [],
      style: [],
      format: [],
      genres: [],
      sort: "popular",
      price: {
        min: filtersList.price.min,
        max: filtersList.price.max,
      },
    }));

    const params = useUrlSearchParams<ProfileShopFiltersParams>("history");

    const activeFilters = ref<ProfileShopFiltersState>(initialFilters);

    const { isProgressAnimationActive, startProgressAnimation } = useProgressAnimation();

    const resetFilters = (filterResetSource: FilterResetSource) => {
      Object.assign(activeFilters.value, resetState.value);
      funnelService.trackEvent("profile_shop_filters_reset", {
        reset_source: filterResetSource,
      });
    };

    const validateFilter = (filterType: keyof ProfileShopFiltersState, value: string = "") => {
      if (filterType === "price") {
        return true;
      }
      const options = filtersList[filterType];
      if (!options.some((option) => option.value === value)) {
        logger.warn(`Invalid filter value: ${value} for filter type: ${filterType}`);
        return false;
      }

      return true;
    };

    const toggleFilter = (
      filterType: keyof ProfileShopFiltersState,
      value: string = "",
      toggleSource: ToggleFilterSource
    ) => {
      if (filterType === "price") {
        activeFilters.value.price = resetState.value.price;
        return;
      }

      if (filterType === "sort") {
        activeFilters.value.sort = value;
        return;
      }

      if (!validateFilter(filterType, value)) {
        return;
      }

      const currentValues = activeFilters.value[filterType];

      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      activeFilters.value[filterType] = newValues;

      if (toggleSource === ToggleFilterSource.QuickFilter) {
        sendFilterEvent("profile_shop_filters_selected", toggleSource);
      }
    };

    const includesFilter = (filterType: FilterKey, value: string = "") => {
      if (filterType === "price") {
        return (
          priceAsNumber(activeFilters.value.price.min) !== priceAsNumber(filtersList.price.min) ||
          priceAsNumber(activeFilters.value.price.max) !== priceAsNumber(filtersList.price.max)
        );
      }

      if (!validateFilter(filterType, value)) {
        return false;
      }

      return activeFilters.value[filterType]?.includes(value) ?? false;
    };

    const updatePriceRange = (value: number[]) => {
      if (value.length !== 2) {
        return;
      }
      activeFilters.value.price = {
        min: getPriceObjFromAmount(value[0], filtersList.price.min.symbol),
        max: getPriceObjFromAmount(value[1], filtersList.price.max.symbol),
      };
    };

    const profileShopRequest = computed(() => ({
      page: 0,
      page_size: PAGE_SIZE,
      filters: activeFilters.value,
    }));

    const profileShopQuery = useQuery({
      ...profileShopQueryOptions(profileShopRequest),
      initialData: initialProfiles,
    });

    const createPriceQuickFilter = (): QuickFilter => ({
      group: "price" as const,
      key: "price",
      label: t("pages.profile-shop.filters.price.range", {
        min: priceDisplay(activeFilters.value.price.min, lang, false),
        max: priceDisplay(activeFilters.value.price.max, lang, false),
      }),
      isActive: includesFilter("price"),
    });

    const createGroupQuickFilter = (group: FilterKey, item: { value: string; label: string }): QuickFilter => ({
      group,
      key: item.value,
      label: item.label,
      isActive: includesFilter(group, item.value),
    });

    const quickFilters = computed<QuickFilter[]>(() => {
      const allFilters = FILTER_GROUPS.flatMap((group: FilterKey) =>
        group === "price"
          ? [createPriceQuickFilter()]
          : filtersList[group].map((item) => createGroupQuickFilter(group, item))
      );

      return allFilters.filter((filter) => filter.isActive || filtersList.quick_filters.includes(filter.key));
    });

    const debouncedRefetch = useDebounceFn(() => {
      Object.assign(params, {
        ...activeFilters.value,
        price: [priceAsNumber(activeFilters.value.price.min), priceAsNumber(activeFilters.value.price.max)],
      });

      profileShopQuery.refetch();
      startProgressAnimation();
    }, DEBOUNCE_TIME);

    watch(
      activeFilters,
      () => {
        debouncedRefetch();
      },
      {
        deep: true,
      }
    );

    const sendFilterEvent = (
      eventName: FilterEventName = "profile_shop_filters_selected",
      toggleSource?: ToggleFilterSource
    ) => {
      funnelService.trackEvent(eventName, {
        page: profileShopRequest.value.page,
        page_size: profileShopRequest.value.page_size,
        type: activeFilters.value.type,
        style: activeFilters.value.style,
        format: activeFilters.value.format,
        genres: activeFilters.value.genres,
        priceMin: priceAsNumber(activeFilters.value.price.min),
        priceMax: priceAsNumber(activeFilters.value.price.max),
        sort: activeFilters.value.sort,
        priceCurrency: filtersList.price.min.symbol,
        number_of_results:
          toggleSource !== ToggleFilterSource.QuickFilter ? profileShopQuery.data.value.total_count : undefined,
        selection_source: toggleSource,
      });
    };

    watch(
      profileShopQuery.data,
      (data) => {
        if (data.total_count < MAX_LOW_RESULTS) {
          sendFilterEvent("profile_shop_low_results");
        }
      },
      { immediate: true }
    );

    return {
      filtersList,
      toggleFilter,
      updatePriceRange,
      resetFilters,
      includesFilter,
      activeFilters,
      isProfilesQueryLoading: profileShopQuery.isLoading,
      isProfilesQueryFetching: profileShopQuery.isFetching,
      profilesQueryData: profileShopQuery.data,
      quickFilters,
      sendFilterSelectedEvent: (toggleSource: ToggleFilterSource) =>
        sendFilterEvent("profile_shop_filters_selected", toggleSource),
      isProgressAnimationActive,
      startProgressAnimation,
    };
  }
);

const useProfileShopFilters = () => {
  const state = _useProfileShopFilters();
  if (!state) {
    throw new Error("useProfileShopFilters must be used within a provider");
  }

  return state;
};

export { provideProfileShopFilters, useProfileShopFilters };
